/**
 * AdjacencyMapPage — Platform page wrapping the adjacency map module.
 *
 * Initializes the API provider, resolves the map ID, and renders
 * the AdjacencyMapApp inside the platform ViewShell context.
 *
 * WQ-097: Phase 4.6.1 Executive Views — Step 3 of 4
 * WQ-342: no auto-create — an empty tenant shows an empty state with an
 * admin-only "Create map" button.
 */

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminContext } from '../../contexts/AdminContextProvider';
import { AdjacencyMapApiProvider, setAuthFetch, setActiveTenantId } from '../../services/adjacency-map-api';
import { useAdjacencyStore } from '../../components/adjacency-map/store/adjacency-store';
import { AdjacencyMapApp } from '../../components/adjacency-map/AdjacencyMapApp';
import AdjacencyMapSyncStatusChip from '../../components/adjacency-map/AdjacencyMapSyncStatusChip';

type PageState = 'loading' | 'ready' | 'empty' | 'error';

const ADMIN_ROLES = new Set(['tenant_admin', 'admin', 'super_admin', 'superadmin']);

export default function AdjacencyMapPage() {
  const { authFetch, user } = useAuth();
  const { activeTenant } = useAdminContext();
  const [state, setState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [mapId, setMapId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const setDataService = useAdjacencyStore((s) => s.setDataService);

  const userRole = (user?.role || '').toLowerCase();
  const userIsAdmin = ADMIN_ROLES.has(userRole);

  // Initialize platform auth and tenant context before any API calls
  useEffect(() => {
    setAuthFetch(authFetch);
    setActiveTenantId(activeTenant?.id || null);
  }, [authFetch, activeTenant]);

  const init = useCallback(async () => {
    try {
      const maps = await AdjacencyMapApiProvider.listMaps();
      if (maps.length > 0) {
        const provider = new AdjacencyMapApiProvider(maps[0].id);
        setDataService(provider);
        setMapId(maps[0].id);
        setState('ready');
      } else {
        // WQ-342 D2(a): a page view never writes. An empty tenant shows the
        // empty state; an admin creates the map explicitly.
        setState('empty');
      }
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Failed to load adjacency maps');
      setState('error');
    }
  }, [setDataService]);

  const createMap = useCallback(async () => {
    setCreating(true);
    try {
      const newMap = await AdjacencyMapApiProvider.createMap('Strategic Adjacency Map');
      setDataService(new AdjacencyMapApiProvider(newMap.id));
      setMapId(newMap.id);
      setState('ready');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Failed to create adjacency map');
      setState('error');
    } finally {
      setCreating(false);
    }
  }, [setDataService]);

  useEffect(() => {
    void init();
  }, [init]);

  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner mb-3" />
          <p style={{ color: 'var(--text-muted)' }}>Loading adjacency map...</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400 mb-2">Failed to load adjacency map</p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{errorMsg}</p>
          <button
            onClick={() => { setState('loading'); void init(); }}
            className="mt-4 px-4 py-2 rounded text-sm"
            style={{ background: 'var(--accent-primary, #3b82f6)', color: '#fff' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (state === 'empty') {
    return (
      <div className="flex items-center justify-center h-64" data-testid="adjacency-map-empty">
        <div className="text-center">
          <p className="mb-2">No adjacency map for this tenant</p>
          {userIsAdmin ? (
            <button
              onClick={() => void createMap()}
              disabled={creating}
              className="mt-2 px-4 py-2 rounded text-sm"
              style={{ background: 'var(--accent-primary, #3b82f6)', color: '#fff' }}
            >
              {creating ? 'Creating…' : 'Create map'}
            </button>
          ) : (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Ask a tenant administrator to create one.
            </p>
          )}
        </div>
      </div>
    );
  }

  const headerExtras = userIsAdmin && mapId ? (
    <AdjacencyMapSyncStatusChip
      mapId={mapId}
      tenantId={activeTenant?.id ?? null}
      authFetch={authFetch}
    />
  ) : null;

  return <AdjacencyMapApp headerExtras={headerExtras} />;
}
